import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Wallet, Wrench, Shield, RefreshCcw, Calendar, Car,
  ChevronRight, Check, Send 
} from 'lucide-react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LongTermPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    duration: '',
    vehicleType: '',
    message: ''
  });

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/vehicles?available=true`);
        setVehicles(response.data);
      } catch (error) {
        console.error('Error fetching vehicles:', error);
      }
    };
    fetchVehicles();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/contact/long-term`, null, {
        params: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: `Duration: ${formData.duration}\nVehicle Type: ${formData.vehicleType}\n\n${formData.message}`
        }
      });
      toast.success('Your inquiry has been submitted. We will contact you shortly.');
      setFormData({ name: '', email: '', phone: '', duration: '', vehicleType: '', message: '' });
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = [
    { 
      icon: Wallet, 
      title: t('longTerm.benefits.savings'),
      desc: t('longTerm.benefits.savingsDesc')
    },
    { 
      icon: Wrench, 
      title: t('longTerm.benefits.maintenance'),
      desc: t('longTerm.benefits.maintenanceDesc')
    },
    { 
      icon: Shield, 
      title: t('longTerm.benefits.insurance'),
      desc: t('longTerm.benefits.insuranceDesc')
    },
    { 
      icon: RefreshCcw, 
      title: t('longTerm.benefits.swap'),
      desc: t('longTerm.benefits.swapDesc')
    },
  ];

  // Calculate monthly rates (approximately 30 days at discounted rate)
  const getMonthlyRate = (vehicle) => {
    // Apply 50% discount on daily rate for monthly rental
    const baseDaily = vehicle.base_daily_rate || 50;
    const discountedDaily = baseDaily * 0.5; // 50% off
    return Math.round(discountedDaily * 30);
  };

  const getOriginalMonthlyRate = (vehicle) => {
    const baseDaily = vehicle.base_daily_rate || 50;
    return Math.round(baseDaily * 30);
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden" data-testid="longterm-hero">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-black/70 z-10" />
          <img
            src="https://images.unsplash.com/photo-1768871413235-22365d491289?w=1920&q=80"
            alt="Long-term rental"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="section-container relative z-20">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.3em] text-primary font-medium mb-4">
              {t('longTerm.subtitle')}
            </p>
            <h1 className="font-serif text-5xl md:text-6xl font-light text-white mb-6">
              {t('longTerm.title')}
            </h1>
            <p className="text-lg text-white/80 leading-relaxed">
              {t('longTerm.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-spacing bg-[#F9FAFB]" data-testid="longterm-benefits">
        <div className="section-container">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl text-[#181B1F] font-light">
              {t('longTerm.benefits.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="card-feature group"
              >
                <benefit.icon 
                  className="w-10 h-10 text-primary mb-4 group-hover:scale-110 transition-transform duration-300" 
                  strokeWidth={1.5} 
                />
                <h3 className="font-serif text-xl text-[#181B1F] mb-2">{benefit.title}</h3>
                <p className="text-gray-500 text-sm">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Monthly Pricing */}
      <section className="section-spacing" data-testid="longterm-pricing">
        <div className="section-container">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl text-[#181B1F] font-light mb-4">
              {t('longTerm.pricing.title')}
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              {i18n.language === 'fr' 
                ? 'Profitez de 50% de réduction sur le tarif journalier pour toute location au mois. Maintenance, assurance et assistance incluses.'
                : 'Enjoy 50% off the daily rate for monthly rentals. Maintenance, insurance, and roadside assistance included.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {vehicles.slice(0, 6).map((vehicle, index) => (
              <div
                key={vehicle.id}
                className="card-vehicle group cursor-pointer"
                onClick={() => navigate(`/vehicles/${vehicle.id}`)}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={vehicle.images[0] || 'https://images.unsplash.com/photo-1692892529205-9f70ecb1df1d?w=600'}
                    alt={vehicle.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wider text-primary">
                      {i18n.language === 'fr' ? vehicle.category_name_fr : vehicle.category_name_en}
                    </span>
                  </div>
                  <h3 className="font-serif text-xl text-[#181B1F]">{vehicle.name}</h3>
                  <div className="flex items-end justify-between pt-2 border-t border-gray-200">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-red-500 text-xs font-semibold bg-red-50 px-2 py-0.5 rounded">-50%</span>
                        <span className="text-gray-400 text-sm line-through">
                          CHF {getOriginalMonthlyRate(vehicle)}
                        </span>
                      </div>
                      <p className="text-2xl text-[#181B1F] font-light">
                        CHF {getMonthlyRate(vehicle)}
                        <span className="text-gray-400 text-sm">{t('longTerm.pricing.monthly')}</span>
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary group-hover:translate-x-1 transition-all duration-300" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="section-spacing bg-[#F9FAFB]" data-testid="longterm-contact">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="font-serif text-4xl text-[#181B1F] font-light mb-6">
                {t('longTerm.contact.title')}
              </h2>
              <p className="text-gray-500 mb-8">
                Get a personalized quote for your long-term rental needs. Our team will prepare a tailored offer based on your requirements.
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-primary" />
                  <span className="text-gray-600">Minimum 30-day rental period</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-primary" />
                  <span className="text-gray-600">All-inclusive monthly payments</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-primary" />
                  <span className="text-gray-600">Free vehicle upgrades (subject to availability)</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-primary" />
                  <span className="text-gray-600">Flexible contract extensions</span>
                </div>
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              className="glass-surface p-8 space-y-6"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-500">Name *</Label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="input-default"
                    placeholder="Your name"
                    data-testid="longterm-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">Email *</Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="input-default"
                    placeholder="your@email.com"
                    data-testid="longterm-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-500">Phone *</Label>
                <Input
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="input-default"
                  placeholder="+41 22 123 45 67"
                  data-testid="longterm-phone"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('longTerm.contact.duration')}</Label>
                  <Select value={formData.duration} onValueChange={(v) => setFormData(prev => ({ ...prev, duration: v }))}>
                    <SelectTrigger className="input-default" data-testid="longterm-duration">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-white shadow-lg border-gray-200">
                      <SelectItem value="1-3 months" className="text-[#181B1F]">1-3 months</SelectItem>
                      <SelectItem value="3-6 months" className="text-[#181B1F]">3-6 months</SelectItem>
                      <SelectItem value="6-12 months" className="text-[#181B1F]">6-12 months</SelectItem>
                      <SelectItem value="12+ months" className="text-[#181B1F]">12+ months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('longTerm.contact.vehicleType')}</Label>
                  <Select value={formData.vehicleType} onValueChange={(v) => setFormData(prev => ({ ...prev, vehicleType: v }))}>
                    <SelectTrigger className="input-default" data-testid="longterm-vehicle">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-white shadow-lg border-gray-200">
                      <SelectItem value="economy" className="text-[#181B1F]">{t('categories.economy')}</SelectItem>
                      <SelectItem value="luxury" className="text-[#181B1F]">{t('categories.luxury')}</SelectItem>
                      <SelectItem value="suv" className="text-[#181B1F]">{t('categories.suv')}</SelectItem>
                      <SelectItem value="sports" className="text-[#181B1F]">{t('categories.sports')}</SelectItem>
                      <SelectItem value="van" className="text-[#181B1F]">{t('categories.van')}</SelectItem>
                      <SelectItem value="electric" className="text-[#181B1F]">{t('categories.electric')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-500">Additional Details</Label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="input-default min-h-[120px]"
                  placeholder="Tell us about your long-term rental needs..."
                  data-testid="longterm-message"
                />
              </div>

              <Button 
                type="submit" 
                disabled={submitting}
                className="w-full btn-primary"
                data-testid="longterm-submit"
              >
                {submitting ? 'Submitting...' : t('longTerm.contact.submit')}
                <Send className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default LongTermPage;
